import * as React from "react";
import { Box, Button, useMediaQuery, useTheme } from "@mui/material";
import CervinoText from "../../typography/CervinoText";
import ShimmerImage from "../../animations/ShimmerImage";

export default function ImageDataCarousel({
                                              items = [],
                                              initialIndex = 0,
                                              orientation = "auto",
                                              height,
                                              onIndexChange,
                                          }) {
    const theme = useTheme();
    const mdDown = useMediaQuery(theme.breakpoints.down("md"));
    const lgUp = useMediaQuery(theme.breakpoints.up("lg"));

    const resolvedOrientation = React.useMemo(() => {
        if (orientation === "horizontal") return "horizontal";
        if (orientation === "vertical") return "vertical";
        return lgUp ? "vertical" : "horizontal";
    }, [orientation, lgUp]);

    const canSwipe = mdDown;
    const count = items.length;

    const clampIndex = React.useCallback(
        (i) => {
            if (!count) return 0;
            return ((i % count) + count) % count;
        },
        [count]
    );

    const [index, setIndex] = React.useState(clampIndex(initialIndex));

    const [anim, setAnim] = React.useState({
        from: clampIndex(initialIndex),
        to: clampIndex(initialIndex),
        phase: "idle",
        dir: 1,
    });

    React.useEffect(() => {
        setIndex((prev) => clampIndex(prev));
        setAnim((a) => ({ ...a, from: clampIndex(a.from), to: clampIndex(a.to) }));
    }, [clampIndex]);

    const goTo = React.useCallback(
        (nextRaw) => {
            if (!count) return;
            const next = clampIndex(nextRaw);
            if (next === index) return;

            const dir = next > index ? 1 : -1;

            setAnim({ from: index, to: next, phase: "animating", dir });
            setIndex(next);
            if (onIndexChange) onIndexChange(next);
        },
        [count, clampIndex, index, onIndexChange]
    );

    const next = React.useCallback(() => goTo(index + 1), [goTo, index]);
    const prev = React.useCallback(() => goTo(index - 1), [goTo, index]);

    // Swipe logic
    const touch = React.useRef({ startX: 0, startY: 0, active: false });

    const onTouchStart = React.useCallback(
        (e) => {
            if (!canSwipe) return;
            const t = e.touches[0];
            touch.current = { startX: t.clientX, startY: t.clientY, active: true };
        },
        [canSwipe]
    );

    const onTouchEnd = React.useCallback(
        (e) => {
            if (!canSwipe || !touch.current.active) return;
            touch.current.active = false;

            const t = e.changedTouches[0];
            const dx = t.clientX - touch.current.startX;
            const dy = t.clientY - touch.current.startY;

            const threshold = 40;
            const absX = Math.abs(dx);
            const absY = Math.abs(dy);

            if (resolvedOrientation === "horizontal") {
                if (absX < threshold || absX < absY) return;
                dx < 0 ? next() : prev();
            } else {
                if (absY < threshold || absY < absX) return;
                dy < 0 ? next() : prev();
            }
        },
        [canSwipe, resolvedOrientation, next, prev]
    );

    const duration = 420;

    const handleTransitionEnd = React.useCallback(() => {
        setAnim((a) =>
            a.phase === "animating" ? { ...a, phase: "idle", from: a.to } : a
        );
    }, []);

    const isAnimating = anim.phase === "animating" && anim.from !== anim.to;

    const currentExit = React.useMemo(() => {
        if (!isAnimating) return "translate3d(0,0,0)";

        if (resolvedOrientation === "horizontal") {
            return anim.dir === 1
                ? "translate3d(-100%,0,0)"
                : "translate3d(100%,0,0)";
        }

        return anim.dir === 1
            ? "translate3d(0,-100%,0)"
            : "translate3d(0,100%,0)";
    }, [isAnimating, resolvedOrientation, anim.dir]);

    const incomingStart = React.useMemo(() => {
        if (!isAnimating) return "translate3d(0,0,0)";

        if (resolvedOrientation === "horizontal") {
            return anim.dir === 1
                ? "translate3d(100%,0,0)"
                : "translate3d(-100%,0,0)";
        }

        return anim.dir === 1
            ? "translate3d(0,100%,0)"
            : "translate3d(0,-100%,0)";
    }, [isAnimating, resolvedOrientation, anim.dir]);

    const renderImageArea = (item) => {
        if (typeof item?.renderImage === "function") {
            return item.renderImage({ item });
        }

        if (!item?.imageUrl) return null;

        return (
            <Box sx={{ mt: 2, width: "100%", display: "flex", justifyContent: "center" }}>
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: 520,
                        height: { xs: 180, sm: 220, md: 260, lg: 300, xl: 300 },
                        borderRadius: 3,
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <ShimmerImage
                        src={item.imageUrl}
                        alt={item.title}
                        width="100%"
                        height="100%"
                        borderRadius={10}
                        objectFit="contain"
                        showBorder
                    />
                </Box>
            </Box>
        );
    };

    const renderSlide = (item) => (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                boxSizing: "border-box",
                p: { xs: 0.5, sm: 1 },
            }}
        >
            <Box
                sx={{
                    height: "100%",
                    width: "100%",
                    borderRadius: 4,
                    borderColor: "divider",
                    boxShadow: theme.shadows[1],
                    p: { xs: 1, sm: 2 },
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden", // safety: prevent any child overflow
                }}
            >
                <CervinoText
                    sx={{
                        fontWeight: 900,
                        fontSize: { xs: 30, sm: 34, md: 38, lg: 40, xl: 40 },
                        color: theme.palette.primary.main,
                        textTransform: "uppercase",
                        textAlign: "left",
                    }}
                >
                    {item.title}
                </CervinoText>

                {item.subtitle && (
                    <CervinoText
                        sx={{
                            fontWeight: 700,
                            fontSize: { xs: 14, sm: 18, md: 20, lg: 24, xl: 24 },
                            color: theme.palette.primary.main,
                            textAlign: "left",
                        }}
                    >
                        {item.subtitle}
                    </CervinoText>
                )}

                {item.description && (
                    <CervinoText
                        sx={{
                            fontWeight: 500,
                            fontSize: { xs: 14, sm: 18, md: 18, lg: 18, xl: 20 },
                            color: "#000",
                            textAlign: "justify",
                        }}
                    >
                        {item.description}
                    </CervinoText>
                )}

                {renderImageArea(item)}
            </Box>
        </Box>
    );

    const currentItem = items[anim.from];
    const incomingItem = items[anim.to];

    return (
        <Box sx={{ width: "100%" }}>
            {/* Navigation (stretched buttons) */}
            <Box sx={{ mb: 2, display: "flex", gap: {xs: 1, sm: 2, md: 3}, flexWrap: "wrap", justifyContent: "center" }}>
                {items.map((item, i) => {
                    const selected = i === index;
                    return (
                        <Button
                            key={`${item.buttonTitle}-${i}`}
                            variant={selected ? "contained" : "outlined"}
                            onClick={() => goTo(i)}
                            sx={{
                                flex: "0 0 auto",
                                borderRadius: 999,
                                textTransform: "none",
                                fontSize: { xs: 10, sm: 14, md: 14, lg: 14, xl: 14 },
                                fontWeight: selected ? 900 : 700,
                                px: {xs: 1, sm: 2},
                                boxShadow: selected ? theme.shadows[1] : "none",
                            }}
                        >
                            {item.buttonTitle}
                        </Button>
                    );
                })}
            </Box>

            {/* Viewport */}
            <Box
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
                sx={{
                    position: "relative",
                    height,
                    overflow: "hidden",
                }}
            >
                {currentItem && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            transform: currentExit,
                            transition: isAnimating
                                ? `transform ${duration}ms cubic-bezier(.2,.8,.2,1)`
                                : "none",
                        }}
                        onTransitionEnd={handleTransitionEnd}
                    >
                        {renderSlide(currentItem)}
                    </Box>
                )}

                {isAnimating && incomingItem && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            transform: incomingStart,
                            animation: `carousel-in-${resolvedOrientation}-${anim.dir} ${duration}ms cubic-bezier(.2,.8,.2,1) forwards`,
                            pointerEvents: "none",
                        }}
                    >
                        {renderSlide(incomingItem)}
                    </Box>
                )}

                <Box
                    sx={{
                        "@keyframes carousel-in-horizontal-1": {
                            from: { transform: "translate3d(100%,0,0)" },
                            to: { transform: "translate3d(0,0,0)" },
                        },
                        "@keyframes carousel-in-horizontal--1": {
                            from: { transform: "translate3d(-100%,0,0)" },
                            to: { transform: "translate3d(0,0,0)" },
                        },
                        "@keyframes carousel-in-vertical-1": {
                            from: { transform: "translate3d(0,100%,0)" },
                            to: { transform: "translate3d(0,0,0)" },
                        },
                        "@keyframes carousel-in-vertical--1": {
                            from: { transform: "translate3d(0,-100%,0)" },
                            to: { transform: "translate3d(0,0,0)" },
                        },
                    }}
                />
            </Box>
        </Box>
    );
}
